// src/utils/encryptUtils.js
import axiosInstance from 'src/api/axiosInstance';
import JSEncrypt from 'jsencrypt';
import CryptoJS from 'crypto-js';

const TTL_MS = 10 * 60 * 1000; // 10 minutes
const mem = new Map();
const inflight = new Map();
let publicKeyCache = null;

// FEATURE FLAG: true => encrypt payload with RSA before sending
// false => send plaintext params (still safe if served over HTTPS)
export const USE_RSA_FOR_TOKEN = true;

/** Fetch and cache RSA public key from backend (if needed) */
async function getPublicKey() {
  if (publicKeyCache) return publicKeyCache;
  const res = await axiosInstance.get(`/rest/crypto/public-key`);
  let key = res.data;
  if (typeof key === 'string' && !key.includes('-----BEGIN')) {
    key = `-----BEGIN PUBLIC KEY-----\n${key}\n-----END PUBLIC KEY-----`;
  }
  publicKeyCache = key;
  return publicKeyCache;
}

/** Encrypt params with RSA before sending (frontend->token endpoint). */
async function encryptParams(params) {
  const pubKey = await getPublicKey();
  const encryptor = new JSEncrypt();
  encryptor.setPublicKey(pubKey);

  const json = JSON.stringify(params);
  const encrypted = encryptor.encrypt(json);

  if (!encrypted) throw new Error('RSA encryption failed');
  return encrypted; // base64
}

/** Normalize params into sorted object */
function normalizeParams(params) {
  if (typeof params === 'number' || typeof params === 'string') {
    return { id: String(params) };
  }
  if (params && typeof params === 'object') {
    return Object.keys(params)
      .sort()
      .reduce((o, k) => ({ ...o, [k]: params[k] }), {});
  }
  throw new Error('Invalid params. Must be number, string, or object.');
}

/** Stable key for hashing */
function keyOf(params) {
  return JSON.stringify(normalizeParams(params));
}

/** Get hashed key (SHA-256) */
function getHashedKey(params) {
  const raw = keyOf(params);
  const hash = CryptoJS.SHA256(raw).toString(CryptoJS.enc.Base64);
  // eslint-disable-next-line prefer-template
  return 'enc:' + hash;
}

/** LocalStorage read with TTL check */
function getStorage(hashedKey) {
  try {
    const raw = localStorage.getItem(hashedKey);
    if (!raw) return null;

    const obj = JSON.parse(raw);
    if (Date.now() - obj.ts > TTL_MS) {
      localStorage.removeItem(hashedKey);
      return null;
    }
    return obj.value;
  } catch {
    return null;
  }
}

/** LocalStorage write */
function setStorage(hashedKey, value) {
  try {
    localStorage.setItem(hashedKey, JSON.stringify({ value, ts: Date.now() }));
  } catch {
    // ignore storage errors
  }
}

/**
 * Fetch encrypted token from backend with caching, RSA protection & de-dupe.
 * @param {number|string|object} params - id or object like {sid:1, cid:2}
 * @param {string} token - JWT
 * @param {boolean} multi - true => /generate-multi-token
 */
export const fetchEncryptedToken = async (params, token, multi = false) => {

  if (!params) {
    return null;
  }

  const stableKey = keyOf(params);
  const hashedKey = getHashedKey(params);

  // 1) In-memory cache
  const memHit = mem.get(hashedKey);
  if (memHit && Date.now() - memHit.ts < TTL_MS) {
    return memHit.value;
  }

  // 2) LocalStorage cache
  const lsHit = getStorage(hashedKey);
  if (lsHit) {
    mem.set(hashedKey, { value: lsHit, ts: Date.now() });
    return lsHit;
  }

  // 3) Deduplicate in-flight requests
  if (inflight.has(hashedKey)) return inflight.get(hashedKey);

  const normalized = normalizeParams(params);

  // Build payload depending on feature flag
  let payload;
  if (USE_RSA_FOR_TOKEN) {
    const encryptedPayload = await encryptParams(normalized);
    payload = { data: encryptedPayload };
  } else {
    payload = normalized;
  }

  const url = multi
    ? `/rest/enctoken/generate-multi-token`
    : `/rest/enctoken/generate-token`;

  const req = axiosInstance
    .post(url, payload, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })
    .then((res) => {
      const value = res.data;
      mem.set(hashedKey, { value, ts: Date.now() });
      setStorage(hashedKey, value);
      inflight.delete(hashedKey);
      return value;
    })
    .catch((err) => {
      inflight.delete(hashedKey);
      throw err;
    });

  inflight.set(hashedKey, req);
  return req;
};

/** Clear caches on logout or user switch */
export const clearEncTokenCache = () => {
  mem.clear();
  inflight.clear();
  publicKeyCache = null;
  Object.keys(localStorage).forEach((k) => {
    if (k.startsWith('enc:')) localStorage.removeItem(k);
  });
};
